import type { CrudFieldConfig } from "components/admin/workspace-crud";
import {
  buildMinMaxValidator,
  buildOptionalEmailValidator,
  buildOptionalPhoneValidator,
} from "components/admin/forms/validation";

import { toSelectOptions } from "pages/app/workspace/workspace-helpers";

export const buildSchoolTeamFields = ({
  ageGroups,
  roleAssignments,
  schoolSports,
}: {
  ageGroups: any[];
  roleAssignments: any[];
  schoolSports: any[];
}): CrudFieldConfig[] => [
  {
    name: "school_sport_id",
    label: "School sport",
    kind: "select",
    options: toSelectOptions(schoolSports, (schoolSport) => schoolSport.sport_name || "Unknown"),
    required: true,
    clearFieldsOnChange: ["sport_age_group_id", "coach_user_id"],
  },
  {
    name: "name",
    label: "Team name",
    placeholder: "1st XI, Colts A, Senior Girls",
    required: true,
  },
  {
    name: "sport_age_group_id",
    label: "Age group",
    kind: "select",
    options: (values) => {
      const selectedSchoolSport = schoolSports.find(
        (schoolSport: any) => String(schoolSport.id) === String(values.school_sport_id),
      );

      return toSelectOptions(
        ageGroups.filter(
          (ageGroup: any) =>
            !selectedSchoolSport ||
            String(ageGroup.sport_id) === String(selectedSchoolSport.sport_id),
        ),
        (ageGroup) => ageGroup.name || "Unknown",
      );
    },
    validate: (value, values) =>
      !String(value || "").trim() && !String(values.age_group_name || "").trim()
        ? "Choose an existing age group or enter a new age group name."
        : undefined,
  },
  {
    name: "age_group_name",
    label: "New age group name",
    placeholder: "Under 19",
  },
  {
    name: "team_label",
    label: "Team label",
    placeholder: "1st XI, A team, Colts",
  },
  {
    name: "min_age",
    label: "Minimum age",
    kind: "number",
    min: 0,
  },
  {
    name: "max_age",
    label: "Maximum age",
    kind: "number",
    min: 0,
    validate: buildMinMaxValidator({
      label: "Maximum age",
      otherField: "min_age",
      relation: "gte",
    }),
  },
  {
    name: "gender_category",
    label: "Gender category",
    kind: "select",
    options: [
      { label: "Mixed", value: "mixed" },
      { label: "Boys", value: "boys" },
      { label: "Girls", value: "girls" },
    ],
    required: true,
  },
  {
    name: "competition_level",
    label: "Competition level",
    placeholder: "first_team, development, regional_league",
  },
  {
    name: "competitive_rank",
    label: "Competitive rank",
    kind: "number",
    min: 1,
  },
  {
    name: "season",
    label: "Season",
    placeholder: "2026",
  },
  {
    name: "coach_user_id",
    label: "Coach",
    kind: "select",
    options: (values) => {
      const selectedSchoolSport = schoolSports.find(
        (schoolSport: any) => String(schoolSport.id) === String(values.school_sport_id),
      );

      return toSelectOptions(
        roleAssignments.filter(
          (assignment: any) =>
            assignment.role === "coach" &&
            (!selectedSchoolSport ||
              String(assignment.sport_id) === String(selectedSchoolSport.sport_id)),
        ),
        (assignment) => assignment.user_name || "Unknown",
        (assignment) => assignment.user_id,
      );
    },
  },
  {
    name: "image",
    label: "Team image",
    kind: "image",
    helper: "Upload a team image or paste a hosted image URL.",
  },
  {
    name: "active",
    label: "Active team",
    kind: "checkbox",
  },
];

export const buildSchoolTeamCreateValues = (schoolSports: any[]) => ({
  school_sport_id: schoolSports?.[0]?.id || "",
  sport_age_group_id: "",
  name: "",
  age_group_name: "",
  min_age: null,
  max_age: null,
  team_label: "",
  gender_category: "mixed",
  competition_level: "",
  competitive_rank: 1,
  season: "",
  coach_user_id: "",
  image: "",
  active: true,
});

export const buildSchoolTeamUpdateValues = (row: any) => {
  const sportAgeGroupId = row.sport_age_group_id || row.sport_age_group?.id || "";

  return {
    school_sport_id: row.school_sport_id || row.school_sport?.id || "",
    sport_age_group_id: sportAgeGroupId,
    name: row.name || "",
    // Only fall back to the display name when the linked age group id is unavailable.
    age_group_name: sportAgeGroupId ? "" : row.age_group || "",
    team_label: row.team_label || "",
    min_age: row.min_age ?? "",
    max_age: row.max_age ?? "",
    gender_category: row.gender_category || "mixed",
    competition_level: row.competition_level || "",
    competitive_rank: row.competitive_rank ?? 1,
    season: row.season || "",
    coach_user_id: row.coach?.id || row.coach_id || "",
    image: row.image_url || row.image || "",
    active: Boolean(row.active),
  };
};

export const buildSchoolPlayerFields = (teams: any[]): CrudFieldConfig[] => [
  {
    name: "first_name",
    label: "First name",
    required: true,
  },
  {
    name: "last_name",
    label: "Last name",
    required: true,
  },
  {
    name: "position",
    label: "Position",
    placeholder: "Opening batter, striker, defender",
  },
  {
    name: "image",
    label: "Player image",
    kind: "image",
    helper: "Upload a player image or paste a hosted image URL.",
  },
  {
    name: "school_team_id",
    label: "Primary school team",
    kind: "select",
    options: toSelectOptions(
      teams,
      (team) => `${team.name || "Unknown"}${team.sport_name ? ` | ${team.sport_name}` : ""}`,
    ),
    required: true,
  },
  {
    name: "school_profile.date_of_birth",
    label: "Date of birth",
    kind: "date",
    required: true,
  },
  {
    name: "school_profile.school_grade",
    label: "School grade",
    placeholder: "10, 11, 12",
    validate: (value) => {
      if (!String(value || "").trim()) {
        return undefined;
      }

      const grade = Number(value);

      if (!Number.isInteger(grade) || grade < 1 || grade > 12) {
        return "School grade must be a whole number between 1 and 12.";
      }

      return undefined;
    },
  },
  {
    name: "school_profile.gender",
    label: "Gender",
    kind: "select",
    options: [
      { label: "Male", value: "male" },
      { label: "Female", value: "female" },
      { label: "Other", value: "other" },
    ],
  },
  {
    name: "school_profile.profile_visibility",
    label: "Profile visibility",
    kind: "select",
    options: [
      { label: "Restricted", value: "restricted" },
      { label: "School only", value: "school_only" },
      { label: "Public", value: "public" },
    ],
    required: true,
  },
  {
    name: "school_profile.guardian_name",
    label: "Guardian name",
    required: true,
  },
  {
    name: "school_profile.guardian_email",
    label: "Guardian email",
    kind: "email",
    validate: buildOptionalEmailValidator("Guardian email"),
  },
  {
    name: "school_profile.guardian_phone",
    label: "Guardian phone",
    validate: buildOptionalPhoneValidator("Guardian phone"),
  },
  {
    name: "school_profile.consent_status",
    label: "Consent status",
    kind: "select",
    options: [
      { label: "Pending", value: "pending" },
      { label: "Granted", value: "granted" },
      { label: "Denied", value: "denied" },
    ],
    required: true,
    clearFieldsOnChange: (nextValue) =>
      String(nextValue) === "granted" ? [] : ["school_profile.consented_at"],
  },
  {
    name: "school_profile.consented_at",
    label: "Consented at",
    kind: "datetime-local",
    hidden: (values) => values.school_profile?.consent_status !== "granted",
    validate: (value, values) =>
      values.school_profile?.consent_status === "granted" && !String(value || "").trim()
        ? "Add the consent timestamp when consent has been granted."
        : undefined,
  },
  {
    name: "school_profile.consent_notes",
    label: "Consent notes",
    kind: "textarea",
    placeholder: "Record guardian notes or consent context.",
  },
];

export const buildSchoolPlayerCreateValues = () => ({
  first_name: "",
  last_name: "",
  position: "",
  image: "",
  school_team_id: "",
  school_profile: {
    date_of_birth: "",
    school_grade: "",
    gender: "",
    profile_visibility: "restricted",
    guardian_name: "",
    guardian_email: "",
    guardian_phone: "",
    consent_status: "pending",
    consented_at: "",
    consent_notes: "",
  },
});

export const buildSchoolPlayerUpdateValues = (row: any) => ({
  first_name: row.first_name || "",
  last_name: row.last_name || "",
  position: row.position || "",
  image: row.avatar_url || row.image_url || row.image || "",
  school_team_id: row.school_teams?.[0]?.id || row.school_team_id || "",
  school_profile: {
    date_of_birth: row.date_of_birth || row.school_profile?.date_of_birth || "",
    school_grade: row.school_grade || row.school_profile?.school_grade || "",
    gender: row.gender || row.school_profile?.gender || "",
    profile_visibility:
      row.profile_visibility || row.school_profile?.profile_visibility || "restricted",
    guardian_name: row.guardian_name || row.school_profile?.guardian_name || "",
    guardian_email: row.guardian_email || row.school_profile?.guardian_email || "",
    guardian_phone: row.guardian_phone || row.school_profile?.guardian_phone || "",
    consent_status: row.consent_status || row.school_profile?.consent_status || "pending",
    consented_at: row.consented_at || row.school_profile?.consented_at || "",
    consent_notes: row.consent_notes || row.school_profile?.consent_notes || "",
  },
});

export const buildSchoolMatchFields = ({
  regions,
  schoolId,
  teamDirectory,
}: {
  regions: any[];
  schoolId: string;
  teamDirectory: any[];
}): CrudFieldConfig[] => [
  {
    name: "home_school_team_id",
    label: "Home team",
    kind: "select",
    options: toSelectOptions(
      teamDirectory.filter((team: any) => !schoolId || String(team.school_id) === String(schoolId)),
      (team) => `${team.name || "Unknown"}${team.school_name ? ` | ${team.school_name}` : ""}`,
    ),
    required: true,
    clearFieldsOnChange: (nextValue, values) =>
      String(values.away_school_team_id || "") === String(nextValue || "")
        ? ["away_school_team_id"]
        : [],
  },
  {
    name: "away_school_team_id",
    label: "Away team",
    kind: "select",
    options: (values) =>
      toSelectOptions(
        teamDirectory.filter((team: any) => String(team.id) !== String(values.home_school_team_id)),
        (team) => `${team.name || "Unknown"}${team.school_name ? ` | ${team.school_name}` : ""}`,
      ),
    required: true,
    validate: (value, values) =>
      String(value || "") && String(value || "") === String(values.home_school_team_id || "")
        ? "Away team must be different from the home team."
        : undefined,
  },
  {
    name: "match_type",
    label: "Match type",
    kind: "select",
    options: [
      { label: "League", value: "league" },
      { label: "Festival", value: "festival" },
      { label: "Friendly", value: "friendly" },
      { label: "Tournament", value: "tournament" },
    ],
    required: true,
  },
  {
    name: "status",
    label: "Status",
    kind: "select",
    options: [
      { label: "Proposed", value: "proposed" },
      { label: "Confirmed", value: "confirmed" },
      { label: "Rejected", value: "rejected" },
      { label: "Completed", value: "completed" },
    ],
    required: true,
  },
  {
    name: "competition_name",
    label: "Competition",
    placeholder: "Johannesburg Schools Festival",
  },
  {
    name: "venue_name",
    label: "Venue name",
    placeholder: "Main Oval",
  },
  {
    name: "venue_address",
    label: "Venue address",
    placeholder: "40 Peter Place, Bryanston",
  },
  {
    name: "administrative_region_id",
    label: "Administrative region",
    kind: "select",
    options: toSelectOptions(regions, (region) => region.name || "Unknown"),
  },
  {
    name: "scheduled_at",
    label: "Scheduled at",
    kind: "datetime-local",
  },
  {
    name: "notes",
    label: "Notes",
    kind: "textarea",
    placeholder: "Travel, officiating, or scheduling notes",
  },
  {
    name: "result_summary",
    label: "Result summary",
    placeholder: "Saints won by 24 runs",
  },
  {
    name: "home_score",
    label: "Home score",
    placeholder: "176/6",
  },
  {
    name: "away_score",
    label: "Away score",
    placeholder: "152/9",
  },
  {
    name: "live_stream_url",
    label: "Live stream URL",
    placeholder: "https://stream.example.com/final",
  },
  {
    name: "live_score_url",
    label: "Live score URL",
    placeholder: "https://scores.example.com/final",
  },
  {
    name: "meeting_url",
    label: "External link URL",
    placeholder: "https://meet.example.com/pre-match",
  },
  {
    name: "external_platform",
    label: "External platform",
    placeholder: "YouTube Live, SuperSport Schools",
  },
  {
    name: "external_stream_label",
    label: "Stream label",
    placeholder: "Watch on SuperSport Schools",
  },
  {
    name: "countdown_enabled",
    label: "Enable countdown",
    kind: "checkbox",
  },
  {
    name: "metadata",
    label: "Metadata",
    kind: "key_value",
    helper: "Optional fields like officiating_status or travel_required.",
  },
];

export const buildSchoolMatchCreateValues = ({
  schoolId,
  teamDirectory,
}: {
  schoolId: string;
  teamDirectory: any[];
}) => ({
  home_school_team_id:
    teamDirectory.find((team: any) => String(team.school_id) === String(schoolId))?.id || "",
  away_school_team_id: "",
  match_type: "league",
  status: "proposed",
  competition_name: "",
  venue_name: "",
  venue_address: "",
  administrative_region_id: "",
  scheduled_at: "",
  notes: "",
  result_summary: "",
  home_score: "",
  away_score: "",
  live_stream_url: "",
  live_score_url: "",
  meeting_url: "",
  external_platform: "",
  external_stream_label: "",
  countdown_enabled: true,
  metadata: {},
});

export const buildSchoolMatchUpdateValues = (row: any) => ({
  home_school_team_id: row.home_school_team?.id || "",
  away_school_team_id: row.away_school_team?.id || "",
  match_type: row.match_type || "league",
  status: row.status || "proposed",
  competition_name: row.competition_name || "",
  venue_name: row.venue_name || "",
  venue_address: row.venue_address || "",
  administrative_region_id: row.administrative_region_id || "",
  scheduled_at: row.scheduled_at || "",
  notes: row.notes || "",
  result_summary: row.result_summary || "",
  home_score: row.home_score || "",
  away_score: row.away_score || "",
  live_stream_url: row.live_links?.stream_url || row.live_stream_url || "",
  live_score_url: row.live_links?.score_url || row.live_score_url || "",
  meeting_url: row.live_links?.meeting_url || row.meeting_url || "",
  external_platform: row.live_links?.platform || row.external_platform || "",
  external_stream_label: row.live_links?.stream_label || row.external_stream_label || "",
  countdown_enabled: row.countdown?.enabled ?? row.countdown_enabled ?? true,
  metadata: row.metadata || {},
});

export const buildSchoolRosterFields = ({
  rosterPlayersByTeam,
  rosterTeams,
}: {
  rosterPlayersByTeam: Record<string, any[]>;
  rosterTeams: any[];
}): CrudFieldConfig[] => [
  {
    name: "school_team_id",
    label: "School team",
    kind: "select",
    options: toSelectOptions(rosterTeams, (team) => team.name || "Unknown"),
    required: true,
    // Players belong to the previous squad, so drop the selections instead of submitting them.
    clearFieldsOnChange: (_nextValue, values) =>
      (Array.isArray(values.players) ? values.players : []).map(
        (_player: any, index: number) => `players.${index}.player_id`,
      ),
  },
  {
    name: "status",
    label: "Status",
    kind: "select",
    options: [
      { label: "Draft", value: "draft" },
      { label: "Submitted", value: "submitted" },
      { label: "Confirmed", value: "confirmed" },
      { label: "Locked", value: "locked" },
    ],
    required: true,
  },
  {
    name: "notes",
    label: "Notes",
    kind: "textarea",
    placeholder: "Selection notes, squad context, or late changes",
  },
  {
    name: "players",
    label: "Players",
    kind: "object_array",
    addLabel: "Add roster player",
    validate: (value) => {
      const players = Array.isArray(value) ? value : [];

      if (!players.length) {
        return "Add at least one roster player.";
      }

      const selectedPlayers = players
        .map((player: any) => String(player?.player_id || "").trim())
        .filter(Boolean);

      if (new Set(selectedPlayers).size !== selectedPlayers.length) {
        return "Each roster player can only be selected once.";
      }

      const captains = players.filter((player: any) => player?.captain);
      const viceCaptains = players.filter((player: any) => player?.vice_captain);

      if (captains.length > 1) {
        return "Only one player can be marked as captain.";
      }

      if (viceCaptains.length > 1) {
        return "Only one player can be marked as vice captain.";
      }

      return undefined;
    },
    createItem: () => ({
      player_id: "",
      position: "",
      jersey_number: "",
      starter: true,
      captain: false,
      vice_captain: false,
      status: "selected",
    }),
    itemFields: [
      {
        name: "player_id",
        label: "Player",
        kind: "select",
        options: (values) =>
          toSelectOptions(
            rosterPlayersByTeam?.[values.school_team_id] || [],
            (player: any) => player.full_name || "Unknown",
          ),
        required: true,
      },
      {
        name: "position",
        label: "Position",
        placeholder: "Opening batter, defender, keeper",
      },
      {
        name: "jersey_number",
        label: "Jersey number",
        placeholder: "7",
      },
      {
        name: "starter",
        label: "Starter",
        kind: "checkbox",
      },
      {
        name: "captain",
        label: "Captain",
        kind: "checkbox",
      },
      {
        name: "vice_captain",
        label: "Vice captain",
        kind: "checkbox",
      },
      {
        name: "status",
        label: "Selection status",
        kind: "select",
        options: [
          { label: "Selected", value: "selected" },
          { label: "Reserve", value: "reserve" },
          { label: "Unavailable", value: "unavailable" },
        ],
      },
    ],
  },
  {
    name: "metadata",
    label: "Metadata",
    kind: "key_value",
    helper: "Optional selection metadata like travel or medical flags.",
  },
];

export const buildSchoolRosterCreateValues = () => ({
  school_team_id: "",
  status: "draft",
  notes: "",
  metadata: {},
  players: [
    {
      player_id: "",
      position: "",
      jersey_number: "",
      starter: true,
      captain: false,
      vice_captain: false,
      status: "selected",
    },
  ],
});

export const buildSchoolRosterUpdateValues = (row: any) => ({
  school_team_id: row.school_team_id || "",
  status: row.status || "draft",
  notes: row.notes || "",
  metadata: row.metadata || {},
  players:
    (row.school_match_roster_players || []).length > 0
      ? (row.school_match_roster_players || []).map((player: any) => ({
          player_id: player.player_id || player.player?.id || "",
          position: player.position || "",
          jersey_number: player.jersey_number || "",
          starter: Boolean(player.starter),
          captain: Boolean(player.captain),
          vice_captain: Boolean(player.vice_captain),
          status: player.status || "selected",
        }))
      : [
          {
            player_id: "",
            position: "",
            jersey_number: "",
            starter: true,
            captain: false,
            vice_captain: false,
            status: "selected",
          },
        ],
});

export const buildPlayerPerformanceFields = ({
  schoolMatches,
  schoolTeams,
  sports,
}: {
  schoolMatches: any[];
  schoolTeams: any[];
  sports: any[];
}): CrudFieldConfig[] => [
  {
    name: "sport_id",
    label: "Sport",
    kind: "select",
    options: toSelectOptions(sports, (sport) => sport.name || "Unknown"),
  },
  {
    name: "school_team_id",
    label: "School team",
    kind: "select",
    options: toSelectOptions(
      schoolTeams,
      (team) => `${team.name || "Unknown"}${team.sport_name ? ` | ${team.sport_name}` : ""}`,
    ),
    clearFieldsOnChange: (nextValue, values) => {
      const selectedMatchId = String(values.school_match_id || "");

      if (!selectedMatchId || !nextValue) {
        return [];
      }

      const stillPlayable = schoolMatches.some(
        (match: any) =>
          String(match.id) === selectedMatchId &&
          (String(match.home_school_team?.id) === String(nextValue) ||
            String(match.away_school_team?.id) === String(nextValue)),
      );

      return stillPlayable ? [] : ["school_match_id"];
    },
  },
  {
    name: "school_match_id",
    label: "School match",
    kind: "select",
    options: (values) =>
      toSelectOptions(
        schoolMatches.filter(
          (match: any) =>
            !values.school_team_id ||
            String(match.home_school_team?.id) === String(values.school_team_id) ||
            String(match.away_school_team?.id) === String(values.school_team_id),
        ),
        (match) =>
          match.competition_name ||
          `${match.home_school_team?.name || "Home"} vs ${match.away_school_team?.name || "Away"}`,
      ),
  },
  {
    name: "source_type",
    label: "Source type",
    kind: "select",
    options: [
      { label: "School", value: "school" },
      { label: "Club", value: "club" },
      { label: "Tournament", value: "tournament" },
      { label: "Festival", value: "festival" },
      { label: "Event", value: "event" },
    ],
    required: true,
  },
  {
    name: "record_type",
    label: "Record type",
    kind: "select",
    options: [
      { label: "Event", value: "event" },
      { label: "Match", value: "match" },
      { label: "Season", value: "season" },
    ],
    required: true,
  },
  {
    name: "source_name",
    label: "Source name",
    placeholder: "Titans Colts, Gauteng Schools Week",
    required: true,
  },
  {
    name: "season",
    label: "Season",
    placeholder: "2026",
  },
  {
    name: "recorded_on",
    label: "Recorded on",
    kind: "date",
    required: true,
  },
  {
    name: "notes",
    label: "Notes",
    kind: "textarea",
    placeholder: "Summarise the performance context or source verification.",
  },
  {
    name: "stats",
    label: "Stats",
    kind: "key_value",
    helper: "Add flexible stat fields like runs, wickets, minutes_played, or awards.",
  },
];

export const buildPlayerPerformanceCreateValues = () => ({
  sport_id: "",
  team_id: "",
  school_team_id: "",
  school_match_id: "",
  source_type: "school",
  record_type: "event",
  source_name: "",
  season: "",
  recorded_on: "",
  notes: "",
  stats: {},
});

export const buildPlayerPerformanceUpdateValues = (row: any) => ({
  sport_id: row.sport_id || "",
  team_id: row.team_id || "",
  school_team_id: row.school_team_id || "",
  school_match_id: row.school_match_id || "",
  source_type: row.source_type || "school",
  record_type: row.record_type || "event",
  source_name: row.source_name || "",
  season: row.season || "",
  recorded_on: row.recorded_on || "",
  notes: row.notes || "",
  stats: row.stats || {},
});
